/**
 * @file: useWavRecording.web.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Browser recorder for speech-to-text. The browser's own recorder writes
 * webm, which the recognizer rejects, so this taps the mic with Web Audio
 * and writes 16 kHz mono 16-bit WAV itself. Same API as the native
 * `useVoiceRecording`, which `useVoiceDictation` uses everywhere else.
 */

import { useCallback, useEffect, useRef } from 'react';
import type { VoiceRecording, VoiceRecordingResult } from './useVoiceRecording';

// The recognizer accepts 8000 or 16000 Hz.
const kSampleRate = 16000;
// Speech RMS is small (~0.05–0.2); scale it into the 0..1 bar range.
const kMeterGain = 4;
const kBufferSize = 4096;

interface Capture {
  context: AudioContext;
  stream: MediaStream;
  processor: ScriptProcessorNode;
  source: MediaStreamAudioSourceNode;
  chunks: Float32Array[];
}

/** Averages the input down to the recognizer's rate. */
function downsample(samples: Float32Array, rate: number): Float32Array {
  if (rate === kSampleRate) return samples;
  const ratio = rate / kSampleRate;
  const output = new Float32Array(Math.floor(samples.length / ratio));
  for (let index = 0; index < output.length; index++) {
    const from = Math.floor(index * ratio);
    const to = Math.min(samples.length, Math.floor((index + 1) * ratio));
    let sum = 0;
    for (let at = from; at < to; at++) sum += samples[at];
    output[index] = sum / Math.max(1, to - from);
  }
  return output;
}

function wavBlob(samples: Float32Array): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const text = (at: number, value: string) => {
    for (let index = 0; index < value.length; index++)
      view.setUint8(at + index, value.charCodeAt(index));
  };
  text(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  text(8, 'WAVE');
  text(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, kSampleRate, true);
  view.setUint32(28, kSampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  text(36, 'data');
  view.setUint32(40, samples.length * 2, true);
  for (let index = 0; index < samples.length; index++) {
    const sample = Math.max(-1, Math.min(1, samples[index]));
    view.setInt16(
      44 + index * 2,
      sample < 0 ? sample * 0x8000 : sample * 0x7fff,
      true,
    );
  }
  return new Blob([view], { type: 'audio/wav' });
}

export const useWavRecording = (opts?: {
  /** Receives a normalized 0..1 input level on each captured buffer. */
  onMeter?: (level: number) => void;
}): VoiceRecording => {
  const captureRef = useRef<Capture | null>(null);
  const onMeterRef = useRef(opts?.onMeter);
  onMeterRef.current = opts?.onMeter;

  const release = useCallback((capture: Capture) => {
    capture.processor.onaudioprocess = null;
    capture.source.disconnect();
    capture.processor.disconnect();
    capture.stream.getTracks().forEach(track => track.stop());
    void capture.context.close().catch(() => undefined);
  }, []);

  const start = useCallback(async () => {
    const previous = captureRef.current;
    captureRef.current = null;
    if (previous) release(previous);
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const Context =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    const context = new Context();
    await context.resume().catch(() => undefined);
    const source = context.createMediaStreamSource(stream);
    const processor = context.createScriptProcessor(kBufferSize, 1, 1);
    const chunks: Float32Array[] = [];
    processor.onaudioprocess = event => {
      const input = event.inputBuffer.getChannelData(0);
      chunks.push(new Float32Array(input));
      let sum = 0;
      for (const sample of input) sum += sample * sample;
      onMeterRef.current?.(
        Math.min(1, Math.sqrt(sum / input.length) * kMeterGain),
      );
    };
    source.connect(processor);
    // A processor only runs while connected to an output; it writes none.
    processor.connect(context.destination);
    captureRef.current = { context, stream, processor, source, chunks };
  }, [release]);

  const stop = useCallback(async (): Promise<VoiceRecordingResult> => {
    const capture = captureRef.current;
    if (!capture) throw new Error('no active recording');
    captureRef.current = null;
    const rate = capture.context.sampleRate;
    release(capture);
    const length = capture.chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const samples = new Float32Array(length);
    let offset = 0;
    for (const chunk of capture.chunks) {
      samples.set(chunk, offset);
      offset += chunk.length;
    }
    return {
      uri: URL.createObjectURL(wavBlob(downsample(samples, rate))),
      format: 'wav',
      sampleRate: kSampleRate,
    };
  }, [release]);

  const cancel = useCallback(async () => {
    const capture = captureRef.current;
    captureRef.current = null;
    if (capture) release(capture);
  }, [release]);

  useEffect(
    () => () => {
      const capture = captureRef.current;
      captureRef.current = null;
      if (capture) release(capture);
    },
    [release],
  );

  return { start, stop, cancel };
};
